// src/pages/mlb/MlbStdStats.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from "recharts";

/** ======= CONFIG ======= */
const BASE = "/data/mlb/std"; // your exporter writes: /data/mlb/std/<YEAR>/*.json
const TABS = [
  { key: "Batting", file: "batting_fangraphs.json" },
  { key: "Pitching", file: "pitching_fangraphs.json" },
];

const SPLITS = {
  Batting: [
    { key: "overall", label: "Overall" },
    { key: "vs_lhp", label: "vs LHP" },
    { key: "vs_rhp", label: "vs RHP" },
  ],
  Pitching: [
    { key: "overall", label: "Overall" },
    { key: "vs_lhb", label: "vs LHB" },
    { key: "vs_rhb", label: "vs RHB" },
  ],
};

const MLB_TEAM_COLORS = {
  ARI: "#A71930",
  ATL: "#CE1141",
  BAL: "#DF4601",
  BOS: "#BD3039",
  CHC: "#0E3386",
  CWS: "#000000",
  CIN: "#C6011F",
  CLE: "#0C2340",
  COL: "#33006F",
  DET: "#0C2340",
  HOU: "#002D62",
  KC: "#004687",
  LAA: "#BA0021",
  LAD: "#005A9C",
  MIA: "#00A3E0",
  MIL: "#12284B",
  MIN: "#002B5C",
  NYM: "#002D72",
  NYY: "#0C2340",
  OAK: "#003831",
  PHI: "#E81828",
  PIT: "#FDB827",
  SD: "#2F241D",
  SEA: "#0C2C56",
  SF: "#FD5A1E",
  STL: "#C41E3A",
  TB: "#092C5C",
  TEX: "#003278",
  TOR: "#134A8E",
  WSH: "#AB0003",
};
const colorForTeam = (t) => MLB_TEAM_COLORS[t] || "#6366F1";

/** ======= HELPERS ======= */
async function fetchJson(url) {
  const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} @ ${url}`);
  const txt = await res.text();
  const ct = res.headers.get("content-type") || "";
  const looksJson = ct.includes("application/json") || /^[\[{]/.test(txt.trim());
  if (!looksJson) throw new Error(`Not JSON @ ${url}`);
  try {
    return JSON.parse(txt);
  } catch {
    throw new Error(`Invalid JSON @ ${url}`);
  }
}

function normalizeRows(rows, season) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => {
    const name = row?.name ?? row?.player_name ?? row?.FG_Name ?? "";
    const team = row?.team ?? row?.Team ?? row?.team_abbr ?? row?.FG_Team ?? "";

    return {
      ...row,
      name,
      season: row?.season ?? Number(season),
      ...(team ? { team } : {}),
    };
  });
}

function orderColumns(cols) {
  const front = ["name", "season", "team"];
  const set = new Set(cols);
  const head = front.filter((c) => set.has(c));
  const tail = cols.filter((c) => !head.includes(c));
  return [...head, ...tail];
}

/** Hide these columns anywhere (case-insensitive) */
const HIDDEN_COLS = new Set(["idgf", "idfg", "dol"]);

/** Force 3-decimal “slash” formatting for these batting-rate stats */
const THREE_DEC_COLS = new Set([
  "avg",
  "obp",
  "slg",
  "ops",
  "iso",
  "babip",
  "woba",
  "xba",
  "xslg",
  "xwoba",
]);

function fmtCell(col, v) {
  if (v == null || v === "") return "";

  const key = String(col || "").toLowerCase();

  // Force .xxx formatting (3 decimals, no leading zero)
  if (typeof v === "number" && THREE_DEC_COLS.has(key)) {
    const s = v.toFixed(3);
    if (s.startsWith("0.")) return s.slice(1); // "0.333" -> ".333"
    if (s.startsWith("-0.")) return "-" + s.slice(2); // "-0.333" -> "-.333"
    return s;
  }

  // Default: keep existing behavior (~2 decimals)
  if (typeof v === "number") {
    const rounded = Math.round(v * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded);
  }

  return String(v);
}

function abbrev(col) {
  const parts = col.split(/(?=[A-Z])|_/).filter(Boolean);
  if (parts.length <= 1) return col.length > 14 ? col.slice(0, 14) + "…" : col;
  let short = parts[0];
  for (let i = 1; i < parts.length && short.length < 12; i++) {
    short += parts[i][0].toUpperCase() + parts[i].slice(1, Math.max(2, 14 - short.length));
  }
  if (short.length > 14) short = short.slice(0, 14);
  if (short !== col) short += "…";
  return short;
}

function exportToCsv(filename, rows, columns) {
  const header = columns.join(",");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const raw = row[col];
          const cell = raw == null ? "" : String(raw);
          return '"' + cell.replaceAll('"', '""') + '"';
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** suffix-insensitive last name (for labels) */
const SUFFIXES = new Set(["JR", "SR", "II", "III", "IV", "V"]);
function baseLastName(name = "") {
  const parts = String(name).replace(/[,\.]/g, " ").replace(/\s+/g, " ").trim().split(" ");
  if (!parts.length) return "";
  const last = parts[parts.length - 1].toUpperCase();
  if (SUFFIXES.has(last) && parts.length >= 2) return parts[parts.length - 2];
  return parts[parts.length - 1];
}
function firstInitial(name = "") {
  const m = String(name).trim().match(/^([A-Za-z])/);
  return m ? `${m[1].toUpperCase()}. ` : "";
}

/** Compute the visible segment for a line y = m x + b clipped to [xMin,xMax]×[yMin,yMax] */
function clipLineToRect(m, b, xMin, xMax, yMin, yMax) {
  const pts = [];

  const yAtXMin = m * xMin + b;
  if (yAtXMin >= yMin && yAtXMin <= yMax) pts.push({ x: xMin, y: yAtXMin });

  const yAtXMax = m * xMax + b;
  if (yAtXMax >= yMin && yAtXMax <= yMax) pts.push({ x: xMax, y: yAtXMax });

  if (Math.abs(m) > 1e-12) {
    const xAtYMin = (yMin - b) / m;
    if (xAtYMin >= xMin && xAtYMin <= xMax) pts.push({ x: xAtYMin, y: yMin });

    const xAtYMax = (yMax - b) / m;
    if (xAtYMax >= xMin && xAtYMax <= xMax) pts.push({ x: xAtYMax, y: yMax });
  }

  const uniq = [];
  for (const p of pts) {
    if (!uniq.some((q) => Math.abs(q.x - p.x) < 1e-9 && Math.abs(q.y - p.y) < 1e-9)) uniq.push(p);
  }
  if (uniq.length < 2) return null;

  let best = [uniq[0], uniq[1]];
  let bestDist = -1;
  for (let i = 0; i < uniq.length; i++) {
    for (let j = i + 1; j < uniq.length; j++) {
      const d = (uniq[i].x - uniq[j].x) ** 2 + (uniq[i].y - uniq[j].y) ** 2;
      if (d > bestDist) {
        bestDist = d;
        best = [uniq[i], uniq[j]];
      }
    }
  }
  return best;
}

function CustomScatterTooltip({ active, payload, xKey, yKey }) {
  if (!active || !payload?.length) return null;

  const p = payload[0]?.payload || {};
  const name = p.name || p._label || "";
  const team = p.team ? ` • ${p.team}` : "";

  const xVal = p?.[xKey];
  const yVal = p?.[yKey];

  return (
    <div
      style={{
        background: "rgba(2, 6, 23, 0.96)",
        border: "1px solid rgba(148, 163, 184, 0.25)",
        borderRadius: 12,
        padding: "10px 12px",
        color: "#F8FAFC",
        boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
        backdropFilter: "blur(6px)",
        minWidth: 170,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 800, color: "#F8FAFC" }}>
        {name}
        <span style={{ fontWeight: 600, color: "#94A3B8" }}>{team}</span>
      </div>

      <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span style={{ color: "#94A3B8", fontSize: 12 }}>{xKey}</span>
          <span style={{ color: "#F8FAFC", fontSize: 12, fontWeight: 700 }}>
            {xVal == null ? "—" : fmtCell(xKey, xVal)}
          </span>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span style={{ color: "#94A3B8", fontSize: 12 }}>{yKey}</span>
          <span style={{ color: "#F8FAFC", fontSize: 12, fontWeight: 700 }}>
            {yVal == null ? "—" : fmtCell(yKey, yVal)}
          </span>
        </div>
      </div>
    </div>
  );
}

/** ======= PAGE ======= */
export default function MlbStdStats() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [tab, setTab] = useState(TABS[0].key);
  const [split, setSplit] = useState("overall");
  const [data, setData] = useState({});
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [meta, setMeta] = useState(null);

  const [view, setView] = useState("table");
  const [visibleByTab, setVisibleByTab] = useState({});

  // Chart controls
  const [xKey, setXKey] = useState("");
  const [yKey, setYKey] = useState("");
  const [gateKey, setGateKey] = useState("");
  const [gateMin, setGateMin] = useState("");

  // Axis & diagonal styling
  const [axisColor, setAxisColor] = useState("#111827");
  const [diagMode, setDiagMode] = useState("off"); // off | yx | means
  const [diagColor, setDiagColor] = useState("#0ea5e9");
  const [diagDash, setDiagDash] = useState("6 6");

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const entries = await Promise.all(
          TABS.map(async (t) => {
            const payload = await fetchJson(`${BASE}/${year}/${t.file}`);
            const rawSplits = payload?.splits && typeof payload.splits === "object" ? payload.splits : {};

            const normalizedSplits = Object.fromEntries(
              Object.entries(rawSplits).map(([splitKey, splitRows]) => [
                splitKey,
                normalizeRows(splitRows, payload?.season ?? year),
              ])
            );

            return [t.key, normalizedSplits];
          })
        );

        // optional updated/meta
        try {
          const u = await fetchJson(`${BASE}/${year}/updated.json`);
          setUpdatedAt(u?.updated || "");
        } catch {
          setUpdatedAt("");
        }
        try {
          const m = await fetchJson(`${BASE}/${year}/meta.json`);
          setMeta(m || null);
        } catch {
          setMeta(null);
        }

        if (dead) return;
        setData(Object.fromEntries(entries));
      } catch (e) {
        if (!dead) setErr(String(e?.message || e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [year]);

  const availableSplits = SPLITS[tab] || [];
  const activeSplit = availableSplits.some((s) => s.key === split) ? split : "overall";
  const rows = data?.[tab]?.[activeSplit] || [];
  const viewKey = `${tab}:${activeSplit}`;

  const allColumns = useMemo(() => {
    if (!rows[0]) return [];
    return orderColumns(Object.keys(rows[0]).filter((c) => !HIDDEN_COLS.has(String(c).toLowerCase())));
  }, [rows]);

  const numericColumns = useMemo(() => {
    if (!rows.length) return [];
    const sample = rows.slice(0, Math.min(30, rows.length));
    return allColumns.filter((c) => sample.every((r) => r[c] == null || typeof r[c] === "number"));
  }, [rows, allColumns]);

  // Set default x/y per tab + split (best-effort)
  useEffect(() => {
    if (!numericColumns.length) {
      setXKey("");
      setYKey("");
      setGateKey("");
      return;
    }

    const lower = (s) => (s || "").toLowerCase();
    const pick = (cands, fb = "") => {
      const n = numericColumns.map(lower);
      const hit = cands.find((c) => n.includes(lower(c)));
      return hit || numericColumns[0] || fb;
    };

    if (tab === "Batting") {
      setXKey((p) => (numericColumns.includes(p) ? p : pick(["hrs", "HR", "iso", "woba", "OPS", "ops"])));
      setYKey((p) => (numericColumns.includes(p) ? p : pick(["woba", "xwoba", "OPS", "ops", "slg"])));
      setGateKey((p) => (numericColumns.includes(p) ? p : pick(["pa", "PA", "abs", "AB", "G"])));
    } else {
      setXKey((p) => (numericColumns.includes(p) ? p : pick(["K_pct", "k_percent", "xFIP", "FIP", "ERA", "era"])));
      setYKey((p) => (numericColumns.includes(p) ? p : pick(["xFIP", "FIP", "ERA", "era", "woba"])));
      setGateKey((p) => (numericColumns.includes(p) ? p : pick(["IP", "GS", "pa", "G"])));
    }
  }, [tab, activeSplit, numericColumns]);

  useEffect(() => {
    if (!allColumns.length) return;
    setVisibleByTab((prev) => (prev[viewKey] ? prev : { ...prev, [viewKey]: allColumns }));
    setSort({ key: allColumns.includes("name") ? "name" : allColumns[0] || "", dir: "asc" });
  }, [viewKey, allColumns]);

  const visibleColumns = visibleByTab[viewKey] || allColumns;
  const nameColIndex = visibleColumns.indexOf("name");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => allColumns.some((c) => String(r?.[c] ?? "").toLowerCase().includes(needle)));
  }, [rows, allColumns, q]);

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    if (!key) return filtered;
    const sign = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
      return String(av ?? "").localeCompare(String(bv ?? "")) * sign;
    });
  }, [filtered, sort]);

  function onHeaderClick(col) {
    setSort((p) => (p.key === col ? { key: col, dir: p.dir === "asc" ? "desc" : "asc" } : { key: col, dir: "asc" }));
  }

  function toggleColumn(col) {
    setVisibleByTab((prev) => {
      const cur = prev[viewKey] || allColumns;
      const next = cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col];
      return { ...prev, [viewKey]: next };
    });
  }

  const selectAll = () => setVisibleByTab((prev) => ({ ...prev, [viewKey]: allColumns }));
  const clearAll = () => setVisibleByTab((prev) => ({ ...prev, [viewKey]: [] }));
  const resetDefault = () => setVisibleByTab((prev) => ({ ...prev, [viewKey]: allColumns }));

  /** ===== Chart computed ===== */
  const { chartRows, xDomain, yDomain, xMean, yMean } = useMemo(() => {
    let out = sorted;

    if (gateKey && gateMin !== "" && !isNaN(Number(gateMin))) {
      out = out.filter((r) => (typeof r[gateKey] === "number" ? r[gateKey] >= Number(gateMin) : true));
    }
    out = out.filter((r) => typeof r[xKey] === "number" && typeof r[yKey] === "number");

    const counts = out.reduce((m, r) => {
      const l = baseLastName(r.name);
      m[l] = (m[l] || 0) + 1;
      return m;
    }, {});
    const withLabels = out.map((r) => {
      const base = baseLastName(r.name);
      const label = counts[base] > 1 ? `${firstInitial(r.name)}${base}` : base;
      return { ...r, _label: label, _fill: colorForTeam(r.team) };
    });

    const xs = withLabels.map((r) => r[xKey]);
    const ys = withLabels.map((r) => r[yKey]);
    const xMean = xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
    const yMean = ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : 0;

    const pad = (min, max) => {
      if (!isFinite(min) || !isFinite(max)) return ["auto", "auto"];
      const range = Math.max(1e-6, max - min);
      const p = range * 0.08;
      return [min - p, max + p];
    };

    if (!withLabels.length) {
      return { chartRows: [], xDomain: ["auto", "auto"], yDomain: ["auto", "auto"], xMean: 0, yMean: 0 };
    }

    const xMin = Math.min(...xs),
      xMax = Math.max(...xs);
    const yMin = Math.min(...ys),
      yMax = Math.max(...ys);

    return { chartRows: withLabels, xDomain: pad(xMin, xMax), yDomain: pad(yMin, yMax), xMean, yMean };
  }, [sorted, gateKey, gateMin, xKey, yKey]);

  const diagSegment = useMemo(() => {
    if (!Array.isArray(xDomain) || !Array.isArray(yDomain)) return null;
    const [xMin, xMax] = xDomain;
    const [yMin, yMax] = yDomain;
    if (![xMin, xMax, yMin, yMax].every((v) => typeof v === "number" && isFinite(v))) return null;
    if (diagMode === "off") return null;

    let m = 1,
      b = 0;
    if (diagMode === "yx") {
      m = 1;
      b = 0;
    } else if (diagMode === "means") {
      if (Math.abs(xMean) > 1e-9) {
        m = yMean / xMean;
        b = 0;
      } else {
        m = 1;
        b = yMean - m * xMean;
      }
    }

    const seg = clipLineToRect(m, b, xMin, xMax, yMin, yMax);
    if (!seg) {
      if (diagMode === "means") {
        const m2 = 1;
        const b2 = yMean - m2 * xMean;
        return clipLineToRect(m2, b2, xMin, xMax, yMin, yMax);
      }
      return null;
    }
    return seg;
  }, [diagMode, xDomain, yDomain, xMean, yMean]);

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    // adjust range whenever you want
    const yrs = [];
    for (let k = y; k >= y - 6; k--) yrs.push(String(k));
    return yrs;
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050816] via-[#050F26] to-[#020617] text-slate-100 px-3 py-6 md:px-6">
      <div className="w-full max-w-screen-2xl mx-auto rounded-2xl border border-slate-800/70 bg-slate-950/80 shadow-2xl backdrop-blur-sm p-4 md:p-6 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-end gap-3 flex-wrap">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-50">MLB Advanced Stats</h1>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-slate-400 mb-0.5">Year</label>
              <select
                className="border border-slate-700 rounded-md px-2 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                title="Select season year"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-[11px] text-slate-400">
            {updatedAt ? <>Last updated: {new Date(updatedAt).toLocaleString()}</> : null}
          </div>
        </div>

        {/* Tabs + view */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setSplit("overall");
                setXKey("");
                setYKey("");
                setGateKey("");
                setGateMin("");
              }}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
                tab === t.key
                  ? "bg-sky-500 text-slate-950 border-sky-400 shadow-sm"
                  : "bg-slate-900/70 border-slate-700 text-slate-200 hover:bg-slate-800"
              }`}
            >
              {t.key}
            </button>
          ))}

          <div className="flex items-center gap-2 ml-1">
            <span className="text-[11px] text-slate-400">Split</span>
            <select
              className="border border-slate-700 rounded-md px-2 py-1.5 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={activeSplit}
              onChange={(e) => {
                setSplit(e.target.value);
                setXKey("");
                setYKey("");
                setGateKey("");
                setGateMin("");
              }}
            >
              {availableSplits.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900/80 p-1">
            {["table", "chart"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs rounded-full transition ${
                  view === v ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-300 hover:bg-slate-800/80"
                }`}
              >
                {v === "table" ? "Table" : "Chart"}
              </button>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <input
            className="border border-slate-700 rounded-md px-3 py-2 text-xs w-full sm:w-80 bg-slate-900/70 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            placeholder="Search any column…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="text-[11px] text-slate-400">
            Rows: {sorted.length.toLocaleString()}
            <span className="ml-2">• {availableSplits.find((s) => s.key === activeSplit)?.label || activeSplit}</span>
            {meta?.season ? <span className="ml-2">• meta season: {meta.season}</span> : null}
          </div>

          {view === "table" && (
            <>
              <details className="relative">
                <summary className="list-none cursor-pointer">
                  <span className="px-3 py-2 rounded-md border border-slate-700 bg-slate-900/80 text-xs font-medium text-slate-100 hover:bg-slate-800">
                    Columns
                  </span>
                </summary>

                <div className="absolute z-50 mt-2 rounded-xl border border-slate-700 bg-slate-950 shadow-xl shadow-slate-900/60 p-3 w-[26rem] sm:w-[34rem] md:w-[40rem]">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100"
                      onClick={(e) => {
                        e.preventDefault();
                        selectAll();
                      }}
                    >
                      Select all
                    </button>
                    <button
                      className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100"
                      onClick={(e) => {
                        e.preventDefault();
                        clearAll();
                      }}
                    >
                      Clear
                    </button>
                    <button
                      className="text-xs px-2 py-1 text-slate-300 hover:text-slate-100 hover:underline"
                      onClick={(e) => {
                        e.preventDefault();
                        resetDefault();
                      }}
                    >
                      Reset
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3 max-h-72 overflow-auto pr-2">
                    {allColumns.map((c) => {
                      const checked = visibleColumns.includes(c);
                      return (
                        <label
                          key={c}
                          className="flex items-start gap-2 text-[13px] px-1 py-1 rounded hover:bg-slate-800/70 cursor-pointer text-slate-100"
                          title={c}
                        >
                          <input
                            type="checkbox"
                            className="accent-sky-500 mt-0.5 w-3.5 h-3.5 flex-shrink-0"
                            checked={checked}
                            onChange={() => toggleColumn(c)}
                          />
                          <span className="leading-tight whitespace-nowrap">{abbrev(c)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </details>

              <button
                className="px-3 py-2 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400 transition border border-sky-400 shadow-sm"
                onClick={() => exportToCsv(`MLB_${year}_${tab}_${activeSplit}.csv`, sorted, visibleColumns)}
              >
                Export CSV
              </button>
            </>
          )}
        </div>

        {err && <div className="text-sm text-rose-400">{err}</div>}

        {view === "table" ? (
          /* ===== TABLE ===== */
          <div className="border border-slate-800 rounded-2xl shadow-xl shadow-slate-900/70 overflow-auto h-[70vh] bg-slate-950/60 backdrop-blur">
            <table className="min-w-max text-xs leading-tight w-full">
              <thead className="bg-slate-900 sticky top-0 z-30">
                <tr>
                  {visibleColumns.map((c, idx) => (
                    <th
                      key={c}
                      className={`text-center px-2 py-2 font-semibold whitespace-nowrap cursor-pointer select-none text-slate-200 ${
                        idx === nameColIndex ? "sticky left-0 z-40 bg-slate-900 border-r border-slate-800" : ""
                      }`}
                      onClick={() => onHeaderClick(c)}
                    >
                      <div className="inline-flex items-center gap-1">
                        <span className="capitalize">{c}</span>
                        {sort.key === c ? (
                          <span className="text-[9px] opacity-60">{sort.dir === "asc" ? "▲" : "▼"}</span>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                      Loading…
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                      No rows.
                    </td>
                  </tr>
                ) : (
                  sorted.map((r, i) => {
                    const rowBg = i % 2 ? "bg-slate-900" : "bg-slate-950";
                    return (
                      <tr key={i} className={rowBg}>
                        {visibleColumns.map((c, j) => (
                          <td
                            key={c}
                            className={`px-2 py-1 whitespace-nowrap text-center text-slate-100 ${j === 0 ? "font-medium" : ""} ${
                              j === nameColIndex ? `sticky left-0 z-20 ${rowBg} border-r border-slate-800` : ""
                            }`}
                          >
                            {fmtCell(c, r[c])}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* ===== CHART ===== */
          <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-950/90 via-slate-950/80 to-slate-900/80 shadow-2xl shadow-slate-900/80">
            {/* Chart Controls */}
            <div className="p-3 border-b border-slate-800 flex flex-wrap items-end gap-3">
              <div className="flex flex-col">
                <label className="text-[11px] text-slate-400 mb-1">X Axis</label>
                <select
                  className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[12rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={xKey}
                  onChange={(e) => setXKey(e.target.value)}
                >
                  {numericColumns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-[11px] text-slate-400 mb-1">Y Axis</label>
                <select
                  className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[12rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={yKey}
                  onChange={(e) => setYKey(e.target.value)}
                >
                  {numericColumns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-[11px] text-slate-400 mb-1">Gate metric (min)</label>
                <div className="flex items-center gap-2">
                  <select
                    className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[12rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    value={gateKey}
                    onChange={(e) => setGateKey(e.target.value)}
                  >
                    <option value="">— none —</option>
                    {numericColumns.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    className="border border-slate-700 rounded-md px-2 py-1 text-xs w-24 bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    placeholder="min"
                    value={gateMin}
                    onChange={(e) => setGateMin(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-end gap-2">
                <div className="flex flex-col">
                  <label className="text-[11px] text-slate-400 mb-1">Axis color</label>
                  <input
                    type="color"
                    className="w-10 h-8 cursor-pointer rounded-md border border-slate-700 bg-slate-900"
                    value={axisColor}
                    onChange={(e) => setAxisColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-end gap-2 ml-auto">
                <div className="flex flex-col">
                  <label className="text-[11px] text-slate-400 mb-1">Diagonal</label>
                  <select
                    className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[10rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    value={diagMode}
                    onChange={(e) => setDiagMode(e.target.value)}
                  >
                    <option value="off">Off</option>
                    <option value="yx">y = x</option>
                    <option value="means">Through means</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] text-slate-400 mb-1">Diag color</label>
                  <input
                    type="color"
                    className="w-10 h-8 cursor-pointer rounded-md border border-slate-700 bg-slate-900"
                    value={diagColor}
                    onChange={(e) => setDiagColor(e.target.value)}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] text-slate-400 mb-1">Diag dash</label>
                  <select
                    className="border border-slate-700 rounded-md px-2 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    value={diagDash}
                    onChange={(e) => setDiagDash(e.target.value)}
                  >
                    <option value="6 6">6 6</option>
                    <option value="4 4">4 4</option>
                    <option value="2 6">2 6</option>
                    <option value="">Solid</option>
                  </select>
                </div>
                <div className="text-[11px] text-slate-400 hidden sm:block">
                  Showing {chartRows.length.toLocaleString()} players
                </div>
              </div>

              <div className="text-[11px] text-slate-400 sm:hidden ml-auto">
                Showing {chartRows.length.toLocaleString()} players
              </div>
            </div>

            {/* Chart */}
            <div className="h-[86vh] md:h-[90vh] min-h-[620px] px-2 pt-2 pb-6">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 12, right: 22, bottom: 40, left: 28 }}>
                  <XAxis
                    type="number"
                    dataKey={xKey}
                    domain={xDomain}
                    tick={{ fontSize: 11, fill: "#CBD5F5" }}
                    tickFormatter={(v) => fmtCell(xKey, v)}
                    label={{
                      value: xKey,
                      position: "bottom",
                      offset: 8,
                      style: { fill: "#9CA3AF", fontSize: 12 },
                    }}
                    axisLine={{ stroke: axisColor, strokeWidth: 1.5 }}
                    tickLine={{ stroke: axisColor }}
                  />

                  <YAxis
                    type="number"
                    dataKey={yKey}
                    domain={yDomain}
                    tick={{ fontSize: 11, fill: "#CBD5F5" }}
                    tickFormatter={(v) => fmtCell(yKey, v)}
                    label={{
                      value: yKey,
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "#9CA3AF", fontSize: 12 },
                    }}
                    axisLine={{ stroke: axisColor, strokeWidth: 1.5 }}
                    tickLine={{ stroke: axisColor }}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: "4 4" }}
                    wrapperStyle={{ outline: "none" }}
                    content={<CustomScatterTooltip xKey={xKey} yKey={yKey} />}
                  />

                  <ReferenceLine
                    x={xMean}
                    stroke="#94A3B8"
                    strokeWidth={1.5}
                    strokeDasharray="6 6"
                    ifOverflow="extendDomain"
                    label={{
                      value: `Mean ${xKey} (${fmtCell(xKey, xMean)})`,
                      position: "top",
                      offset: 12,
                      fill: "#9CA3AF",
                      fontSize: 12,
                    }}
                  />
                  <ReferenceLine
                    y={yMean}
                    stroke="#94A3B8"
                    strokeWidth={1.5}
                    strokeDasharray="6 6"
                    ifOverflow="extendDomain"
                    label={{
                      value: `Mean ${yKey} (${fmtCell(yKey, yMean)})`,
                      position: "right",
                      offset: 12,
                      fill: "#9CA3AF",
                      fontSize: 12,
                    }}
                  />

                  {diagSegment && (
                    <ReferenceLine segment={diagSegment} stroke={diagColor} strokeWidth={1.75} strokeDasharray={diagDash} />
                  )}

                  <Scatter
                    data={chartRows}
                    shape={({ cx, cy, payload }) => (
                      <g>
                        <circle cx={cx} cy={cy} r={6} fill={payload._fill} opacity={0.9} />
                        <circle cx={cx} cy={cy} r={6} fill="none" stroke="white" strokeWidth={1.5} />
                      </g>
                    )}
                    activeDot={{ r: 7, strokeWidth: 1.5 }}
                  >
                    <LabelList dataKey="_label" position="top" style={{ fontSize: 10, fill: "#E5E7EB" }} />
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            <div className="px-4 pb-4 -mt-2 text-center text-sm text-slate-300">
              {year} {tab} {availableSplits.find((s) => s.key === activeSplit)?.label || activeSplit} — {xKey} vs {yKey}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}